package net.bothosting.docs.pages.create_a_discord_bot

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initHow_toPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("create_a_discord_bot/how_to.md", FrontMatterElement.Builder { addScalar("description", "This guide explains the steps to create your bot. Keep in mind that this guide is unrelated to bot-hosting.net, and we do not help with code or libraries."); addScalar("icon", "robot"); }))
}

@Page("/create_a_discord_bot/how-to")
@Layout(".components.layouts.DocsLayout")
@Composable
fun How_toPage() {
    org.jetbrains.compose.web.dom.H1(attrs = { id("how-to-create-a-discord-bot") }) {
        org.jetbrains.compose.web.dom.Text("How to Create a Discord Bot")
    }
    org.jetbrains.compose.web.dom.H4(attrs = { id("step-1-learn-a-programming-language") }) {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Step 1: Learn a Programming Language")
        }
        org.jetbrains.compose.web.dom.Text(" ")
        com.varabyte.kobweb.compose.dom.GenericTag<org.w3c.dom.Element>("a", attrs = {attr("href", "#ys46es5t60k");attr("id", "ys46es5t60k")}) {
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Before you start thinking about your bot, you need to know how to program. Do not focus on bot-related topics yet. Focus on learning a language first.")
        org.jetbrains.compose.web.dom.Br()
        org.jetbrains.compose.web.dom.Text("Which language should you start with? That is your choice, although most beginners start with Python or JavaScript.")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("Python:")
                }
            }
            org.jetbrains.compose.web.dom.Text(" ")
            com.varabyte.kobweb.silk.components.navigation.Link("https://www.codecademy.com/learn/python") {
                org.jetbrains.compose.web.dom.Text("https://www.codecademy.com/learn/python")
            }
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("JavaScript:")
                }
            }
            org.jetbrains.compose.web.dom.Text(" ")
            com.varabyte.kobweb.silk.components.navigation.Link("https://www.codecademy.com/learn/javascript") {
                org.jetbrains.compose.web.dom.Text("https://www.codecademy.com/learn/javascript")
            }
        }
    }
    com.varabyte.kobweb.compose.dom.GenericTag("blockquote") {
        org.jetbrains.compose.web.dom.P {
            org.jetbrains.compose.web.dom.Text("A well-known server that helps with programming language questions and development issues is The Coding Den: ")
            com.varabyte.kobweb.silk.components.navigation.Link("https://discord.com/invite/code") {
                org.jetbrains.compose.web.dom.Text("https://discord.com/invite/code")
            }
        }
    }
    org.jetbrains.compose.web.dom.H4(attrs = { id("step-2-learn-to-use-a-library") }) {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Step 2: Learn to use a Library")
        }
        org.jetbrains.compose.web.dom.Text(" ")
        com.varabyte.kobweb.compose.dom.GenericTag<org.w3c.dom.Element>("a", attrs = {attr("href", "#id-32287a6pb5y");attr("id", "id-32287a6pb5y")}) {
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Once ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("you know how to program")
        }
        org.jetbrains.compose.web.dom.Text(", check out the Discord API server. If you do not know how to code yet, go back to Step 1. Then find a channel for the language you know. Note that this server will not teach you how to code. For questions unrelated to the library, see The Coding Den linked in Step 1.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Library Comparison:")
        }
        org.jetbrains.compose.web.dom.Text(" ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://discordapi.com/unofficial/comparison.html") {
            org.jetbrains.compose.web.dom.Text("https://discordapi.com/unofficial/comparison.html")
        }
        org.jetbrains.compose.web.dom.Br()
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Discord API:")
        }
        org.jetbrains.compose.web.dom.Text(" ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://discord.com/invite/discord-api") {
            org.jetbrains.compose.web.dom.Text("https://discord.com/invite/discord-api")
        }
    }
    org.jetbrains.compose.web.dom.H4(attrs = { id("step-3-design-features") }) {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Step 3: Design Features")
        }
        org.jetbrains.compose.web.dom.Text(" ")
        com.varabyte.kobweb.compose.dom.GenericTag<org.w3c.dom.Element>("a", attrs = {attr("href", "#id-70e22bskt83");attr("id", "id-70e22bskt83")}) {
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("So, your bot is running. Fantastic. But what should it do? Start simple. Make a command that responds with \"pong\" when a user types \"ping\". Work your way up from there by adding complexity and unique features. Add whatever you like, but make sure to follow the practices linked above. If you need ideas, try joining development servers for larger bots. You can often see which features people request and build your bot to fill those needs.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Do you prefer starting with pre made bases insted? Check out ")
        com.varabyte.kobweb.silk.components.navigation.Link("/community_resources/useful-repositories") {
            org.jetbrains.compose.web.dom.Text("useful-repositories.md")
        }
    }
    org.jetbrains.compose.web.dom.H4(attrs = { id("step-4-host-the-bot") }) {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Step 4: Host the Bot")
        }
        org.jetbrains.compose.web.dom.Text(" ")
        com.varabyte.kobweb.compose.dom.GenericTag<org.w3c.dom.Element>("a", attrs = {attr("href", "#fxffzw60x24");attr("id", "fxffzw60x24")}) {
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("bot-hosting.net is a free 24/7 service that lets you host your bots. You can find all the information you need to create a server and set it up in our wiki, ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://wiki.bot-hosting.net/") {
            org.jetbrains.compose.web.dom.Text("https://wiki.bot-hosting.net/")
        }
        org.jetbrains.compose.web.dom.Text(", or on this site.")
        org.jetbrains.compose.web.dom.Br()
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Please note:")
        }
        org.jetbrains.compose.web.dom.Text(" The Discord server is only for help with Step 4. We cannot and will not help with coding-related issues or requests.")
    }
    com.varabyte.kobweb.silk.components.display.Callout(type = com.varabyte.kobweb.silk.components.display.CalloutType.WARNING, label = null, variant = com.varabyte.kobweb.silk.components.display.CalloutDefaults.Variant) {
        org.jetbrains.compose.web.dom.P {
            org.jetbrains.compose.web.dom.Text(" Please keep in mind that this guide is unrelated to bot-hosting.net, and you will not get help with your code or libraries.")
        }
    }
}
